import * as React from 'react'
import { Formik, FieldProps, Field } from 'formik'
import isEmail from 'validator/lib/isEmail'

import { utils as authUtils } from '@owl-nest/auth'
import * as env from '@owl-nest/config'
import * as service from '@owl-nest/services'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'
import { ua } from '@owl-nest/utils'

import { apiErrorGetter, validateRequired } from './validators'
import { memorizeThirdPartyProvider } from './thirdPartyProvider'
import { useAuthFailureMessage } from './useAuthFailureMessage'

import * as S from './styles'

type Form = { identifier: string }

type Translations = { cta: string }

type UnknownUserProps = {
  allowGuest?: boolean
  auth: service.user.UseAuthUnknown
  showHint?: boolean
  translations?: Translations
}

export function UnknownUser({
  allowGuest,
  auth,
  showHint = true,
  translations,
}: UnknownUserProps): React.ReactElement<UnknownUserProps> {
  useAuthFailureMessage(auth)

  const [error, setError] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  const getApiErrors = apiErrorGetter<{ email: string; username: string }>(auth.failure)

  const isWebView = ua.isWebview(window.navigator.userAgent)

  const originUrl = document !== undefined ? document.location.href : undefined

  return (
    <>
      <Formik<Form>
        initialValues={{ identifier: '' }}
        onSubmit={(values) => {
          if (isEmail(authUtils.getImpostorFreeEmail(values.identifier).email)) {
            setLoading(true)
            auth.identify('email', values.identifier, allowGuest)
          } else if (!allowGuest) {
            auth.identify('username', values.identifier)
            setLoading(true)
          } else {
            setLoading(false)
            setError(t('This email is not valid, please correct it.'))
          }
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleSubmit, validateField }) => {
          const apiError = getApiErrors({
            email: {
              ValueError: t('Enter a valid email address'),
              // FIXME: maybe API should use a better classification than InvalidValueError for bounce email
              InvalidValueError: t("This email is invalid and doesn't seem to be reachable"),
            },
            username: { ValueError: t('Enter a valid username: Letters, digits and - _ only') },
          })

          const label = allowGuest ? t('E-mail address') : t('E-mail address or username')

          const hasApiError = apiError('email') !== undefined || apiError('username') !== undefined

          return (
            <S.UnknownUserForm noValidate onSubmit={handleSubmit}>
              {!allowGuest && showHint && (
                <plume.styles.copy.M.light>
                  {t('To continue, fill in your email or choose to log in with Facebook or Google.')}
                </plume.styles.copy.M.light>
              )}
              <Field name="identifier" validate={validateRequired}>
                {({ field, meta }: FieldProps<string>) => {
                  React.useEffect(() => {
                    validateField(field.name)
                  }, [field.value])
                  return (
                    <plume.TextField
                      label={label}
                      placeholder={t('E-mail address or username')}
                      type="email"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={
                        error
                          ? error
                          : meta.touched
                            ? meta.error || apiError('email') || apiError('username')
                            : undefined
                      }
                    />
                  )
                }}
              </Field>
              <plume.RoundButton loading={loading && !hasApiError} fitToParent type="submit">
                {allowGuest && translations ? translations.cta : t('Continue')}
              </plume.RoundButton>
              {!allowGuest && (
                <>
                  <S.ButtonsSeparator>
                    <S.HorizontalRuler />
                    <S.OrLabel>{t('Or')}</S.OrLabel>
                  </S.ButtonsSeparator>
                  <plume.SocialButton
                    onClick={() => {
                      memorizeThirdPartyProvider('facebook')
                      window.location.href = `${env.FACEBOOK_LOGIN_URL}?next=${window.location.href}`
                    }}
                    platform="facebook"
                    type="button"
                    style="round"
                  >
                    {t('Log in with Facebook')}
                  </plume.SocialButton>
                  {!isWebView && (
                    <>
                      <plume.SocialButton
                        onClick={() => {
                          memorizeThirdPartyProvider('google')
                          window.location.href = `${env.GOOGLE_LOGIN_URL}?next=${window.location.href}`
                        }}
                        platform="google"
                        type="button"
                        style="round"
                      >
                        {t('Log in with Google')}
                      </plume.SocialButton>
                    </>
                  )}
                </>
              )}
              <plume.Link href={`${env.PASSWORD_RESET_URL}?origin_url=${originUrl}`}>
                {t('Forgot your password?')}
              </plume.Link>
            </S.UnknownUserForm>
          )
        }}
      </Formik>
    </>
  )
}
